// src/domain/setlist.js
// Reglas del setlist sin DOM ni Supabase: formato de impresión, búsqueda en el
// repertorio, cabecera, nombre del archivo y las operaciones del editor. Así se
// pueden probar solas (tests/setlist.domain.test.js) y el editor, el Word y la
// vista previa imprimen exactamente lo mismo.
//
// Formato del contenido (igual que la columna jsonb, ver la migración
// 20261019_setlists.sql):
//   { v: 1, blocks: [ { lines: [
//       { type: "songs", important, songs: [ { song_id, key, capo, print_note } ] },
//       { type: "text", text, internal_note } ] } ] }
//   key   null = tono habitual; valor = tono solo en este setlist.
//   capo  null = cejilla habitual; 0 = sin cejilla aquí; 1-12 = traste aquí.

import { STAGES } from "./constants.js";

export const SETLIST_CONTENT_VERSION = 1;

// Fases en las que el evento tiene setlist (en producción o ya realizado).
export const SETLIST_STAGES = Object.freeze([STAGES.CONFIRMED, STAGES.DONE]);

// Notación americana con sostenidos, como en los setlists de la banda. La
// base de datos rechaza cualquier otro valor (songs_key_check).
export const SETLIST_KEYS = Object.freeze([
  "C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B",
  "Cm", "C#m", "Dm", "D#m", "Em", "Fm", "F#m", "Gm", "G#m", "Am", "A#m", "Bm",
]);

export const CAPO_FRETS = Object.freeze([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]);

// Mismos límites que las restricciones de la base de datos.
export const SETLIST_LIMITS = Object.freeze({
  songTitle: 120,
  songArtist: 120,
  songNotes: 200,
  headerNames: 120,
  printNote: 30,
  textLine: 60,
  internalNote: 200,
});

const norm = (v) => String(v ?? "").trim();

export function isSetlistStage(stage) {
  return SETLIST_STAGES.includes(norm(stage));
}

// ─── Normalización ──────────────────────────────────────────────────────────
// Espejo exacto de title_norm / artist_norm en SQL: mismo mapa de tildes,
// minúsculas y todo lo que no sea letra o número pasa a un espacio. Si las dos
// reglas divergieran, el editor dejaría pasar un duplicado que la base de
// datos rechaza.
const ACCENTS_FROM = "ÁÀÂÄÃÉÈÊËÍÌÎÏÓÒÔÖÕÚÙÛÜÑÇáàâäãéèêëíìîïóòôöõúùûüñç";
const ACCENTS_TO = "AAAAAEEEEIIIIOOOOOUUUUNCaaaaaeeeeiiiiooooouuuunc";
const ACCENT_MAP = new Map([...ACCENTS_FROM].map((ch, i) => [ch, ACCENTS_TO[i]]));

export function normalizeSongText(value) {
  const translated = [...String(value ?? "")].map(ch => ACCENT_MAP.get(ch) ?? ch).join("");
  return translated.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

// Texto tal y como se guarda: forma Unicode compuesta (para que la base de
// datos compare igual "é" tecleada de dos formas) y sin espacios de sobra.
export function cleanText(value) {
  return String(value ?? "").normalize("NFC").replace(/\s+/g, " ").trim();
}

export function upperEs(value) {
  return String(value ?? "").toLocaleUpperCase("es-ES");
}

// ─── Repertorio ─────────────────────────────────────────────────────────────

export function songsById(songs) {
  return new Map((songs || []).map(s => [s.id, s]));
}

export function songLabel(song) {
  if (!song) return "Canción no encontrada";
  return `${song.title} — ${song.artist}`;
}

// Busca en título, artista y nota interna, sin tildes ni mayúsculas ("mil
// campanas" encuentra Ni tú ni nadie). Primero lo que empieza igual que lo
// buscado, luego lo que lo contiene en el título y al final el resto.
export function searchSongs(songs, query, { limit = 30, includeInactive = false } = {}) {
  const active = (songs || []).filter(s => includeInactive || s.is_active !== false);
  const q = normalizeSongText(query);
  const byTitle = (a, b) => String(a.title).localeCompare(String(b.title), "es", { sensitivity: "base" });
  if (!q) return [...active].sort(byTitle).slice(0, limit);

  const tokens = q.split(" ");
  const ranked = [];
  for (const song of active) {
    const title = normalizeSongText(song.title);
    const haystack = `${title} ${normalizeSongText(song.artist)} ${normalizeSongText(song.notes)}`;
    if (!tokens.every(t => haystack.includes(t))) continue;
    const rank = title.startsWith(q) ? 0
      : title.includes(q) ? 1
      : tokens.every(t => title.includes(t)) ? 2
      : 3;
    ranked.push({ song, rank });
  }
  ranked.sort((a, b) => a.rank - b.rank || byTitle(a.song, b.song));
  return ranked.slice(0, limit).map(r => r.song);
}

// Revisa los datos de una canción nueva antes de enviarla. Devuelve los
// errores por campo, la canción ya existente si es un duplicado y los datos
// limpios listos para guardar.
export function validateSongInput(input, existingSongs = []) {
  const title = cleanText(input?.title);
  const artist = cleanText(input?.artist);
  const songKey = norm(input?.song_key) || null;
  const capoRaw = input?.capo_fret;
  const capo = capoRaw === null || capoRaw === undefined || capoRaw === "" ? null : Number(capoRaw);
  const notes = cleanText(input?.notes) || null;

  const errors = {};
  if (!title) errors.title = "El título es obligatorio.";
  else if (title.length > SETLIST_LIMITS.songTitle) errors.title = `Máximo ${SETLIST_LIMITS.songTitle} caracteres.`;
  if (!artist) errors.artist = "El artista es obligatorio.";
  else if (artist.length > SETLIST_LIMITS.songArtist) errors.artist = `Máximo ${SETLIST_LIMITS.songArtist} caracteres.`;
  if (songKey && !SETLIST_KEYS.includes(songKey)) errors.song_key = "Tono no válido.";
  if (capo !== null && !CAPO_FRETS.includes(capo)) errors.capo_fret = "La cejilla va del traste 1 al 12.";
  if (notes && notes.length > SETLIST_LIMITS.songNotes) errors.notes = `Máximo ${SETLIST_LIMITS.songNotes} caracteres.`;

  let duplicate = null;
  if (title && artist) {
    const t = normalizeSongText(title);
    const a = normalizeSongText(artist);
    duplicate = (existingSongs || []).find(s => normalizeSongText(s.title) === t && normalizeSongText(s.artist) === a) || null;
  }

  return {
    ok: Object.keys(errors).length === 0 && !duplicate,
    errors,
    duplicate,
    clean: { title, artist, song_key: songKey, capo_fret: capo, notes },
  };
}

// ─── Contenido ──────────────────────────────────────────────────────────────

export function emptyContent() {
  return { v: SETLIST_CONTENT_VERSION, blocks: [] };
}

export function cloneContent(content) {
  return JSON.parse(JSON.stringify(content ?? emptyContent()));
}

function normalizeSongItem(raw) {
  const songId = norm(raw?.song_id);
  if (!songId) return null;
  const key = SETLIST_KEYS.includes(raw?.key) ? raw.key : null;
  const capoNum = raw?.capo === null || raw?.capo === undefined ? null : Number(raw.capo);
  const capo = capoNum === 0 || CAPO_FRETS.includes(capoNum) ? capoNum : null;
  const printNote = cleanText(raw?.print_note).slice(0, SETLIST_LIMITS.printNote) || null;
  return { song_id: songId, key, capo, print_note: printNote };
}

function normalizeLine(raw) {
  if (raw?.type === "text") {
    return {
      type: "text",
      text: String(raw.text ?? "").slice(0, SETLIST_LIMITS.textLine),
      internal_note: cleanText(raw.internal_note).slice(0, SETLIST_LIMITS.internalNote) || null,
    };
  }
  if (raw?.type === "songs") {
    const songs = (Array.isArray(raw.songs) ? raw.songs : []).map(normalizeSongItem).filter(Boolean);
    if (!songs.length) return null;
    return { type: "songs", important: Boolean(raw.important), songs };
  }
  return null;
}

// Convierte lo que venga de la base de datos en un contenido bien formado,
// descartando lo que no se entienda en vez de romper el editor.
export function normalizeContent(raw) {
  const blocks = Array.isArray(raw?.blocks) ? raw.blocks : [];
  return {
    v: SETLIST_CONTENT_VERSION,
    blocks: blocks.map(b => ({
      lines: (Array.isArray(b?.lines) ? b.lines : []).map(normalizeLine).filter(Boolean),
    })),
  };
}

// Lo que se guarda: sin líneas de texto vacías ni bloques vacíos.
export function prepareContentForSave(content) {
  const normalized = normalizeContent(content);
  const blocks = normalized.blocks
    .map(b => ({
      lines: b.lines
        .map(l => (l.type === "text" ? { ...l, text: cleanText(l.text) } : l))
        .filter(l => l.type !== "text" || l.text),
    }))
    .filter(b => b.lines.length);
  return { v: SETLIST_CONTENT_VERSION, blocks };
}

export function contentStats(content) {
  const stats = { blocks: 0, lines: 0, songs: 0, textLines: 0 };
  for (const block of content?.blocks || []) {
    stats.blocks++;
    for (const line of block.lines || []) {
      stats.lines++;
      if (line.type === "text") stats.textLines++;
      else stats.songs += (line.songs || []).length;
    }
  }
  return stats;
}

// Canciones que ya están en el setlist, para no ofrecerlas otra vez al añadir.
export function songIdsInContent(content) {
  const ids = new Set();
  for (const block of content?.blocks || []) {
    for (const line of block.lines || []) {
      if (line.type !== "songs") continue;
      for (const item of line.songs || []) ids.add(item.song_id);
    }
  }
  return ids;
}

// Problemas que impiden guardar o descargar. Devuelve [] si está todo bien.
export function validateContent(content, songsMap, { requireSongs = true } = {}) {
  const problems = [];
  const stats = contentStats(content);
  if (requireSongs && stats.songs === 0) {
    problems.push({ code: "empty", message: "El setlist no tiene ninguna canción." });
  }
  const missing = new Set();
  for (const block of content?.blocks || []) {
    for (const line of block.lines || []) {
      if (line.type !== "songs") continue;
      for (const item of line.songs || []) {
        if (!songsMap?.has(item.song_id)) missing.add(item.song_id);
      }
    }
  }
  if (missing.size) {
    problems.push({
      code: "missing_song",
      message: missing.size === 1
        ? "Hay una canción que ya no está en el repertorio. Cámbiala por otra."
        : `Hay ${missing.size} canciones que ya no están en el repertorio. Cámbialas por otras.`,
    });
  }
  return problems;
}

// ─── Impresión ──────────────────────────────────────────────────────────────

export function effectiveKey(item, song) {
  return item?.key ?? song?.song_key ?? null;
}

// capo 0 en el setlist quita la cejilla aunque la canción la lleve.
export function effectiveCapo(item, song) {
  if (item?.capo === 0) return null;
  return item?.capo ?? song?.capo_fret ?? null;
}

// Lo que va entre paréntesis tras el título: tono, cejilla y nota impresa.
export function songParenText(item, song) {
  const capo = effectiveCapo(item, song);
  const parts = [
    effectiveKey(item, song),
    capo ? `Capo${capo}` : null,
    item?.print_note ? upperEs(item.print_note) : null,
  ].filter(Boolean);
  return parts.length ? `(${parts.join(", ")})` : "";
}

// Una línea del setlist en tramos de texto con su negrita:
//   _ZAPATILLAS + BESOS (ECDL)  ->  "_" "ZAPATILLAS" " + " "BESOS" " (ECDL)"
// El título, el "_" y el "+" van en negrita; el paréntesis, en normal.
export function lineRuns(line, songsMap) {
  if (line?.type === "text") return [{ text: upperEs(cleanText(line.text)), bold: true }];
  const runs = [];
  if (line?.important) runs.push({ text: "_", bold: true });
  (line?.songs || []).forEach((item, i) => {
    const song = songsMap?.get(item.song_id);
    if (i > 0) runs.push({ text: " + ", bold: true });
    runs.push({ text: upperEs(song?.title || "¿Canción?"), bold: true });
    const paren = songParenText(item, song);
    if (paren) runs.push({ text: ` ${paren}`, bold: false });
  });
  return runs;
}

// Filas imprimibles: una por línea y una en blanco entre bloques. Las líneas de
// texto vacías y los bloques que se quedan sin nada no salen, igual que al
// guardar (prepareContentForSave), para que la vista previa no engañe.
export function buildPrintableRows(content, songsMap) {
  const blocks = (content?.blocks || [])
    .map(block => (block.lines || [])
      .filter(line => line.type !== "text" || cleanText(line.text))
      .map(line => ({ type: "line", runs: lineRuns(line, songsMap) })))
    .filter(rows => rows.length);
  return blocks.flatMap((rows, i) => (i > 0 ? [{ type: "blank" }, ...rows] : rows));
}

export function rowsToText(rows) {
  return (rows || []).map(r => (r.type === "blank" ? "" : r.runs.map(run => run.text).join(""))).join("\n");
}

// ─── Cabecera y archivo ─────────────────────────────────────────────────────

const MONTHS_ES = ["enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

// "2026-10-10" -> "10 de octubre de 2026". Se lee el texto directamente, sin
// pasar por Date, para no cambiar de día por la zona horaria.
export function formatLongDateEs(isoDate) {
  const m = norm(isoDate).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return "";
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return "";
  return `${day} de ${MONTHS_ES[month - 1]} de ${m[1]}`;
}

const DATE_IN_TEXT = new RegExp(
  `\\b\\d{1,2}\\s+(?:de\\s+)?(?:${MONTHS_ES.join("|")})(?:\\s+(?:de\\s+)?\\d{4})?\\b`, "gi");

// Propone los nombres de la cabecera a partir del nombre del evento, que hoy
// se escribe de muchas formas: "Boda Madrid | Meritxell y Pablo", "Ana e
// Iñigo (canarias)", "Boda Yashmine y Gorka - Viñedos de Butrón". Es solo una
// propuesta: el campo se puede corregir antes de guardar.
export function suggestHeaderNames(eventName) {
  const original = cleanText(eventName);
  let s = original;
  if (s.includes("|")) s = s.slice(s.lastIndexOf("|") + 1);
  s = s.replace(/\([^)]*\)/g, " ");
  s = s.replace(/\s[-–—]\s.*$/, "");
  s = s.replace(/^\s*boda\s+(?:de\s+)?/i, "");
  s = s.replace(DATE_IN_TEXT, " ");
  s = s.replace(/\b\d{1,2}[/.-]\d{1,2}[/.-]\d{2,4}\b/g, " ");
  s = cleanText(s);
  return (s || original).slice(0, SETLIST_LIMITS.headerNames);
}

// Trozo de nombre de archivo: sin tildes, Ñ como N, en mayúsculas y con "_".
export function slugForFileName(value) {
  return String(value ?? "")
    .normalize("NFD").replace(/[̀-ͯ]/g, "")
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

// Lugar para el nombre del archivo: ciudad del venue principal, si no la del
// evento y si no la región.
export function pickSetlistPlace({ venueCity, eventCity, regionLabel } = {}) {
  return norm(venueCity) || norm(eventCity) || norm(regionLabel) || "";
}

// "20261010_GRAN_CANARIA_ANA_E_INIGO.docx"
export function buildSetlistFileName({ dateIso, place, names } = {}) {
  const date = norm(dateIso).slice(0, 10).replace(/-/g, "");
  const parts = [
    /^\d{8}$/.test(date) ? date : "",
    slugForFileName(place).slice(0, 40),
    slugForFileName(names).slice(0, 60),
  ].map(p => p.replace(/^_+|_+$/g, "")).filter(Boolean);
  return `${parts.length ? parts.join("_") : "SETLIST"}.docx`;
}

// ─── Operaciones del editor ─────────────────────────────────────────────────
// Todas devuelven un contenido nuevo y no tocan el recibido. Las posiciones son
// índices: bi = bloque, li = línea, si = canción dentro de la línea. Una
// posición que no existe deja el contenido igual.

function lineAt(content, bi, li) {
  return content?.blocks?.[bi]?.lines?.[li] ?? null;
}

function newSongItem(songId) {
  return { song_id: songId, key: null, capo: null, print_note: null };
}

export function addBlock(content, atIndex) {
  const next = cloneContent(content);
  const i = atIndex === undefined ? next.blocks.length : Math.max(0, Math.min(atIndex, next.blocks.length));
  next.blocks.splice(i, 0, { lines: [] });
  return next;
}

export function removeBlock(content, bi) {
  const next = cloneContent(content);
  if (bi < 0 || bi >= next.blocks.length) return next;
  next.blocks.splice(bi, 1);
  return next;
}

export function moveBlock(content, bi, delta) {
  const next = cloneContent(content);
  const to = bi + delta;
  if (bi < 0 || bi >= next.blocks.length || to < 0 || to >= next.blocks.length) return next;
  const [block] = next.blocks.splice(bi, 1);
  next.blocks.splice(to, 0, block);
  return next;
}

export function addSongLine(content, bi, songId, atIndex) {
  const next = cloneContent(content);
  const block = next.blocks[bi];
  if (!block || !norm(songId)) return next;
  const i = atIndex === undefined ? block.lines.length : Math.max(0, Math.min(atIndex, block.lines.length));
  block.lines.splice(i, 0, { type: "songs", important: false, songs: [newSongItem(songId)] });
  return next;
}

export function addTextLine(content, bi, text = "", atIndex) {
  const next = cloneContent(content);
  const block = next.blocks[bi];
  if (!block) return next;
  const i = atIndex === undefined ? block.lines.length : Math.max(0, Math.min(atIndex, block.lines.length));
  block.lines.splice(i, 0, { type: "text", text: String(text).slice(0, SETLIST_LIMITS.textLine), internal_note: null });
  return next;
}

export function removeLine(content, bi, li) {
  const next = cloneContent(content);
  if (!lineAt(next, bi, li)) return next;
  next.blocks[bi].lines.splice(li, 1);
  return next;
}

// Mueve una línea a otra posición, también a otro bloque (arrastrar).
export function moveLine(content, from, to) {
  const next = cloneContent(content);
  if (!lineAt(next, from?.bi, from?.li)) return next;
  const target = next.blocks[to?.bi];
  if (!target) return next;
  const [line] = next.blocks[from.bi].lines.splice(from.li, 1);
  const i = Math.max(0, Math.min(to.li, target.lines.length));
  target.lines.splice(i, 0, line);
  return next;
}

// Subir o bajar una línea (móvil). En el borde de un bloque pasa al anterior
// o al siguiente, para poder cambiarla de bloque sin arrastrar.
export function moveLineBy(content, bi, li, delta) {
  if (!lineAt(content, bi, li) || (delta !== -1 && delta !== 1)) return cloneContent(content);
  const block = content.blocks[bi];
  if (delta === -1) {
    if (li > 0) return moveLine(content, { bi, li }, { bi, li: li - 1 });
    if (bi > 0) return moveLine(content, { bi, li }, { bi: bi - 1, li: content.blocks[bi - 1].lines.length });
  } else {
    if (li < block.lines.length - 1) return moveLine(content, { bi, li }, { bi, li: li + 1 });
    if (bi < content.blocks.length - 1) return moveLine(content, { bi, li }, { bi: bi + 1, li: 0 });
  }
  return cloneContent(content);
}

// "Unir con la de abajo": las canciones de la línea siguiente pasan a esta,
// tocadas seguidas con "+". Solo entre líneas de canciones del mismo bloque.
export function joinWithNext(content, bi, li) {
  const next = cloneContent(content);
  const line = lineAt(next, bi, li);
  const below = lineAt(next, bi, li + 1);
  if (line?.type !== "songs" || below?.type !== "songs") return next;
  line.songs.push(...below.songs);
  line.important = line.important || below.important;
  next.blocks[bi].lines.splice(li + 1, 1);
  return next;
}

// "Separar": cada canción de la línea vuelve a su propia línea. La marca de
// importante se queda en la primera.
export function splitLine(content, bi, li) {
  const next = cloneContent(content);
  const line = lineAt(next, bi, li);
  if (line?.type !== "songs" || line.songs.length < 2) return next;
  const parts = line.songs.map((item, i) => ({ type: "songs", important: i === 0 && line.important, songs: [item] }));
  next.blocks[bi].lines.splice(li, 1, ...parts);
  return next;
}

export function addSongToLine(content, bi, li, songId) {
  const next = cloneContent(content);
  const line = lineAt(next, bi, li);
  if (line?.type !== "songs" || !norm(songId)) return next;
  line.songs.push(newSongItem(songId));
  return next;
}

// Quitar la última canción de una línea elimina la línea.
export function removeSongFromLine(content, bi, li, si) {
  const next = cloneContent(content);
  const line = lineAt(next, bi, li);
  if (line?.type !== "songs" || si < 0 || si >= line.songs.length) return next;
  line.songs.splice(si, 1);
  if (!line.songs.length) next.blocks[bi].lines.splice(li, 1);
  return next;
}

// Cambiar una canción por otra. Tono, cejilla y nota eran de la anterior, así
// que se reinician.
export function replaceSong(content, bi, li, si, songId) {
  const next = cloneContent(content);
  const line = lineAt(next, bi, li);
  if (line?.type !== "songs" || !line.songs[si] || !norm(songId)) return next;
  line.songs[si] = newSongItem(songId);
  return next;
}

// Tono, cejilla y nota solo para este setlist. Lo que no se pasa no cambia.
export function setSongOverrides(content, bi, li, si, changes = {}) {
  const next = cloneContent(content);
  const line = lineAt(next, bi, li);
  const item = line?.type === "songs" ? line.songs[si] : null;
  if (!item) return next;
  if ("key" in changes) item.key = SETLIST_KEYS.includes(changes.key) ? changes.key : null;
  if ("capo" in changes) {
    const c = changes.capo === null || changes.capo === undefined || changes.capo === "" ? null : Number(changes.capo);
    item.capo = c === 0 || CAPO_FRETS.includes(c) ? c : null;
  }
  if ("print_note" in changes) item.print_note = cleanText(changes.print_note).slice(0, SETLIST_LIMITS.printNote) || null;
  return next;
}

export function toggleImportant(content, bi, li) {
  const next = cloneContent(content);
  const line = lineAt(next, bi, li);
  if (line?.type !== "songs") return next;
  line.important = !line.important;
  return next;
}

export function setTextLine(content, bi, li, changes = {}) {
  const next = cloneContent(content);
  const line = lineAt(next, bi, li);
  if (line?.type !== "text") return next;
  if ("text" in changes) line.text = String(changes.text ?? "").slice(0, SETLIST_LIMITS.textLine);
  if ("internal_note" in changes) {
    line.internal_note = cleanText(changes.internal_note).slice(0, SETLIST_LIMITS.internalNote) || null;
  }
  return next;
}
